<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="false"
                :showExtraBtn="true"
                :showAddNew="true"
                title="Dental Schedule"
                extraBtnTitle="List View"
                @onClickExtraBtn="navigateToListPage"
                btnTitle="Add New"
                @onClickAddNewButton="onClickToAppointment"
            />
        </div>

        <div class="px-2 pb-2">
          <div class="row">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Doctor</label>
              <AsyncSelect
                  placeholder="Select Doctor"
                  v-model="contactProfileId"
                  :api-service="fetchContactProfiles"
                  :additional-query="additionalQuery"
              />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Schedule Type</label>
              <v-select
                  placeholder="Select Schedule Status"
                  :options="activityLogTypeList"
                  v-model="activityLogType"
                  label="name"
                  :reduce="name => name.value"
              />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Schedule Status</label>
              <v-select
                  placeholder="Select Schedule Status"
                  :options="taskCalendarStatusList"
                  v-model="taskCalendarStatus"
                  label="name"
                  :reduce="name => name.value"
              />
            </div>

            <div class="col-sm-2 col-md-2 d-flex align-items-end mt-2 action-btn-setter">
              <button
                  style="min-width: 64px;"
                  @click="onEventSubmitted"
                  class="btn btn-primary btn-block waves-effect waves-float waves-light"
              >GO
              </button>
            </div>
          </div>
        </div>

        <div class="app-calendar overflow-hidden border">
            <div class="row no-gutters">
                <div class="col position-relative">
                    <div class="card shadow-none border-0 mb-0 rounded-0">
                        <div class="card-body pb-0" v-show="!loading">
                          <CalendarComponent
                              :defaultView="'timeGridWeek'"
                              :calendarOptions="calendarOptions"
                              ref="calendarRef"
                          />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Loader v-if="loading"/>

      <DentalScheduleModal
          v-if="store.state.isModalOpenThree"
          :singleActivity="singleActivity"
          @submitted="onEventSubmitted"
        />

      <AddAppointmentModal
          v-if="store.state.isModalOpen"
          @onSubmit="onEventSubmitted"
      />

    </div>
</template>

<script setup>
import {ref, reactive, inject, nextTick, onMounted} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import CalendarComponent from '@/components/atom/calendar/CalendarComponent.vue';
import TitleButton from '@/components/atom/TitleButton';
import Loader from '@/components/atom/LoaderComponent';
import { getCurrentDateTime, taskStatusColors } from '@/services/utils/global.js';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from '@/services/modules/contact'
import useDate from '@/services/utils/day'
import handleDentalOs from '@/services/modules/dental-os/dentalOs';
import DentalScheduleModal from '@/components/molecule/company/dental-os/dental-schedule/DentalScheduleModal.vue';
import AddAppointmentModal from '@/components/molecule/company/dental-os/appointment/AddAppointmentModal.vue';
import handleChartAccount from '@/services/modules/chartAccount'

const { fetchDentalScheduleList, fetchSingleDentalSchedule, updateDragDropDentalSchedule } = handleDentalOs();
const { fetchContactProfiles } = handleContact()
const { formatDateTime, formatToDate } = useDate()
const { fetchHome } = handleChartAccount()

const showError = inject('showError');
const showSuccess = inject('showSuccess');
const additionalQuery = {
  roles: "doctor,medical_officer",
}
const route = useRoute();
const router = useRouter();
const store = useStore();

const loading = ref(false);
const currentEvents = ref([]);
const singleActivity = ref(null);
const calendarRef = ref(null);
const contactProfileId = ref('');
const startDate = ref('');
const endDate = ref('');
const contactProfile = ref('');
const taskCalendarStatusList = ref([]);
const taskCalendarStatus = ref('');
const activityLogTypeList = ref([]);
const activityLogType = ref('');

const calendarOptions = reactive({
  initialEvents: [],
  editable: true,
  selectable: true,
  selectMirror: true,
  dayMaxEvents: true,
  weekends: true,
  eventClick: handleEventClick,
  eventsSet: handleEvents,
  events: async function(info, successCallback, failureCallback) {
    try {
      let selectedMonthStart = info.startStr.substring(0, 10);
      let selectedMonthEnd = info.endStr.substring(0, 10);

      let query = getQuery();
      if (selectedMonthStart) query += '&start_date=' + selectedMonthStart;
      if (selectedMonthEnd) query += '&end_date=' + selectedMonthEnd;

      const res = await fetchDentalScheduleList(query);
      if (!res.status) {
        successCallback([]);
        return;
      }
      const events = res.data.map(item => {
        let color = taskStatusColors[item.status] || { bg: '#6b7280', border: '#4b5563', text: '#ffffff' };

        return {
          id: item.id,
          status: item.status,
          title: item.patient_name + '(' + item.product_name + ')',
          start: getCurrentDateTime(item.start_time),
          end: item.end_time ? getCurrentDateTime(item.end_time) : null,
          backgroundColor: color.bg,
          borderColor: color.border,
          textColor: color.text,
        };
      });

      successCallback(events);
    } catch (error) {
      failureCallback(error);
    }
  },
  eventDrop: async function(info) {
    await updateTaskDateTime(info);
  },
  eventResize: async function(info) {
    await updateTaskDateTime(info);
  }
});

async function updateTaskDateTime(info) {
  const event = info.event;

  const payload = {
    id: event.id,
    start_time: event.start ? formatDateTime(event.start.toISOString()) : null,
    end_time: event.end ? formatDateTime(event.end.toISOString()) : null,
    date: event.start ? formatToDate(event.start.toISOString()) : null,
    company_id: route.params.companyId
  };

  try {
    const res = await updateDragDropDentalSchedule(payload.id, payload);
    if (!res.status) {
      info.revert();
      return showError(res.message || "Failed to update task.");
    }
    showSuccess(res.message);
  } catch (err) {
    info.revert();
    showError("Something went wrong while updating task.");
  }
}

const onClickToAppointment = () => {
  store.state.isModalOpen = true
}

const onEventSubmitted = () => {
  let calendarApi = null;
  if (
      calendarRef.value &&
      typeof calendarRef.value.getApi === 'function'
  ) {
    calendarApi = calendarRef.value.getApi();
  }

  if (calendarApi) {
    calendarApi.refetchEvents();
  }
};

const getQuery = () => {
  const companyId = route.params.companyId;
  let query = `?company_id=${companyId}&is_all=1`;
  if (contactProfileId.value) query += `&contact_profile_id=${contactProfileId.value.id}`;
  if (taskCalendarStatus.value) query += `&status=${taskCalendarStatus.value}`;
  if (activityLogType.value) query += `&type=${activityLogType.value}`;
  return query;
}

function navigateToListPage() {
  router.push({name: `dental-schedule-list`, params: route.params, query: route.query});
}

async function handleEventClick(clickInfo) {
  const activityId = clickInfo.event.id;
  if (!activityId) return;
  loading.value = true;
  singleActivity.value = null;
  const calendarApi = calendarRef.value && calendarRef.value.getApi();

  try {
    const res = await fetchSingleDentalSchedule( getQuery(), activityId);
    if (!res.status) return showError(res.message);
    let response = res.data;
    singleActivity.value = response;

    if (calendarApi && response.start_time) {
      const dateObj = new Date(response.start_time.replace(" ", "T"));
      await nextTick();
      calendarApi.gotoDate(dateObj);
    }

    store.state.isModalOpenThree = true;
  } finally {
    loading.value = false;
  }
}

function handleEvents(events) {
  currentEvents.value = events;
}

onMounted(() => {
  Promise.all([
    fetchHome().then(res => {
      if (!res.data) {
        return;
      }
      if (res.data && res.data.task_calendar_status){
        taskCalendarStatusList.value = res.data.task_calendar_status;
      }
      if (res.data && res.data.activityLogs_types){
        activityLogTypeList.value = res.data.activityLogs_types.filter(item =>
            ['dental_appointment', 'treatment_plan'].includes(item.value)
        )
      }
    })
  ])
})

</script>
